import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import { AuthProvider } from '@praxis/component-auth'
import AppHeader from './components/AppHeader'
import FormNavbar from './components/FormNavbar'
import Footer from './components/Footer'
import Details from './components/Details'
import GuestDetails from './components/GuestDetails'
import apiConfig from './apiConfig'
import { OpenTelemetryManager } from '@measurement/opentelemetry-web'
import { clearAllGuestWifiState } from './utils/tmAuthStorage'

export const App = () => {
  const [config, setConfig] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const otelInitializedRef = useRef(false)

  //  NEW: keep one canonical callback URL for both /tm and /auth/login.
  const loginRedirectUrl = `${window.location.origin}/auth/login`

  const handleLogout = () => {
    clearAllGuestWifiState()
    setIsAuthenticated(false)
    window.location.href = apiConfig.auth.logoutUrl
  }

  const handleSetAuthenticated = useCallback((isAuth) => {
    setIsAuthenticated(isAuth)
  }, [])

  useEffect(() => {
    let isMounted = true

    const initializeOTel = (publicConfig) => {
      if (!otelInitializedRef.current && publicConfig?.otelApiKey) {
        OpenTelemetryManager.initialize({
          apiKey: publicConfig.otelApiKey,
          serviceName: publicConfig.otelServiceName || 'guestwifiauth',
        })
        otelInitializedRef.current = true
      }
    }

    // SECURITY: only minimal public config is injected into the page.
    // The backend API key stays server-side and is added by the proxy.

    if (!window.__GUESTWIFI_CONFIG__) {
      console.error(
        '[FATAL] Config not found in window.__GUESTWIFI_CONFIG__. ' +
          'The server must inject config into index.html at startup. ' +
          'Check docker-entrypoint.sh (production) or development config injection setup.',
      )
      return
    }

    const data = window.__GUESTWIFI_CONFIG__
    if (isMounted) {
      setConfig(data)
      initializeOTel(data)
    }

    return () => {
      isMounted = false
    }
  }, [])

  if (!config) return null

  return (
    <Router>
      <AppHeader onLogout={handleLogout} isAuthenticated={isAuthenticated} />

      <Routes>
        <Route path="/" element={<FormNavbar config={config} />} />

        <Route
          path="/tm"
          element={
            <AuthProvider
              env={config}
              {...apiConfig.auth}
              //  CHANGED: put explicit props AFTER the spread so apiConfig.auth
              // cannot override the callback URL or core auth config.
              clientId={apiConfig.auth.clientId}
              authorizationUrl={apiConfig.auth.authorizationUrl}
              logoutUrl={apiConfig.auth.logoutUrl}
              loginRedirect={loginRedirectUrl}
            >
              <Details
                config={config}
                setAuthenticated={handleSetAuthenticated}
              />
            </AuthProvider>
          }
        />

        <Route path="/guest" element={<GuestDetails />} />

        <Route
          path="/auth/login"
          element={
            <AuthProvider
              env={config}
              {...apiConfig.auth}
              //  CHANGED: callback route must use the exact same redirect
              // configuration as the /tm route or session hydration can fail.
              clientId={apiConfig.auth.clientId}
              authorizationUrl={apiConfig.auth.authorizationUrl}
              logoutUrl={apiConfig.auth.logoutUrl}
              loginRedirect={loginRedirectUrl}
            >
              <Details
                config={config}
                setAuthenticated={handleSetAuthenticated}
              />
            </AuthProvider>
          }
        />

        {/* L4: catch-all — redirect unknown routes back to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Footer />
    </Router>
  )
}

export default App
